import { AIResponse } from './types';

import { supabase } from '@/common/config/supabase';
import handleException from '@/common/utils/errors';
import i18n from '@/i18n';

/**
 * Generates subtasks for a given task via the Supabase Edge Function
 * @param taskContent The content of the parent task
 * @param maxSubtasks Maximum number of subtasks to generate (default: 4)
 * @returns A promise that resolves to an array of subtask strings
 */
export const generateSubtasks = async (
  taskContent: string,
  maxSubtasks: number = 4,
): Promise<AIResponse<string[]>> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      return {
        data: null as unknown as string[],
        error: 'User not authenticated',
      };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const response = await fetch(`${supabaseUrl}/functions/v1/ai-checklist-breakdown`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        taskContent,
        maxSubtasks,
        language: i18n.language,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      return {
        data: null as unknown as string[],
        error: err.error || `HTTP error! status: ${response.status}`,
      };
    }

    const data: string[] = await response.json();
    const subtasks = Array.isArray(data) ? data.slice(0, maxSubtasks) : [];
    return { data: subtasks, error: null };
  } catch (error) {
    handleException(error);
    return {
      data: null as unknown as string[],
      error: error instanceof Error ? error.message : 'Failed to generate subtasks',
    };
  }
};
